// @ts-nocheck — legacy v1 editor model kept only for the v1→v2 migration tooling and its tests.
import type {
  V1ChoiceItem as ChoiceItem,
  V1ChoiceOption as ChoiceOption,
  V1PhoneCallItem as PhoneCallItem,
  V1PhoneChatItem as PhoneChatItem,
  V1PhoneChatReplyOption as PhoneChatReplyOption,
  V1Scenario as Scenario,
  V1Scene as Scene,
  V1ContentItem as ContentItem,
} from '../../systems/legacy-v1-types';
import { applyAutoConvergeToPhoneReplies, collectPhoneReplyGotoScenes } from '../../systems/legacy-phone-branch';
import { isMissionStartScene } from '../../systems/mission-progress';
import { getContentItemLabel, getContentItemSummary, uniqueId } from './utils';

export interface InlineBlockRef {
  blockId: string;
  sceneId: string;
  contentIndex: number;
  label: string;
  summary: string;
}

/** Block in the flat chapter script (no scene boundaries in the UI). */
export interface ChapterFlatBlock {
  blockId: string;
  item: ContentItem;
  sceneId: string;
  contentIndex: number;
  flatIndex: number;
}

export type BranchingOption = ChoiceOption | PhoneChatReplyOption;
export type PhoneReplyBranchItem = PhoneCallItem | PhoneChatItem;
export type BranchingContentItem = ChoiceItem | PhoneReplyBranchItem;
export type PhoneReplySegmentKind = 'phoneCall' | 'phoneChat';

export function isPhoneReplyItem(item: ContentItem): item is PhoneReplyBranchItem {
  return item.type === 'phoneCall' || item.type === 'phoneChat';
}

export function phoneReplySegmentKind(item: PhoneReplyBranchItem): PhoneReplySegmentKind {
  return item.type === 'phoneChat' ? 'phoneChat' : 'phoneCall';
}

export function isPhoneSegmentItem(item: ContentItem): item is PhoneReplyBranchItem {
  return isPhoneReplyItem(item);
}

export function isInlineBranchingItem(item: ContentItem): item is ChoiceItem {
  return item.type === 'choice';
}

function isChapterSegmentBoundary(item: ContentItem): boolean {
  return isInlineBranchingItem(item) || isPhoneSegmentItem(item);
}

export function getBranchingOptions(item: ContentItem | undefined): BranchingOption[] | null {
  if (!item) return null;
  if (item.type === 'choice') return item.options;
  if (isPhoneReplyItem(item) && item.replyOptions?.length) return item.replyOptions;
  return null;
}

export function isBranchSceneId(sceneId: string): boolean {
  if (sceneId.includes('__blk_')) return true;
  const sep = sceneId.indexOf('--');
  return sep > 0 && sep < sceneId.length - 2;
}

/** Slug lisible pour l'id d'une scène de branche (ex. « Choix 1 » → choix-1). */
export function slugifyBranchSceneName(text: string): string {
  return (
    text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'branche'
  );
}

export function branchSceneIdForOption(
  sourceSceneId: string,
  optionText: string,
  optionIndex: number,
  usedIds: Set<string>,
  keepId?: string
): string {
  if (keepId && isBranchSceneId(keepId) && !keepId.includes('__blk_')) {
    return keepId;
  }

  let slug = slugifyBranchSceneName(optionText);
  if (!slug) slug = `option-${optionIndex + 1}`;

  let candidate = `${sourceSceneId}--${slug}`;
  let suffix = 2;
  while (usedIds.has(candidate)) {
    candidate = `${sourceSceneId}--${slug}-${suffix++}`;
  }
  return candidate;
}

export function syncBlockIdsForContent(
  inlineBlockIds: string[] | undefined,
  contentLength: number
): string[] {
  const ids = [...(inlineBlockIds ?? [])];
  while (ids.length < contentLength) {
    ids.push(uniqueId('blk'));
  }
  ids.length = contentLength;
  return ids;
}

export function ensureSceneBlockIds(scene: Scene): Scene {
  const inlineBlockIds = syncBlockIdsForContent(scene.inlineBlockIds, scene.content.length);
  if (
    scene.inlineBlockIds &&
    inlineBlockIds.length === scene.inlineBlockIds.length &&
    inlineBlockIds.every((id, i) => id === scene.inlineBlockIds![i])
  ) {
    return scene;
  }
  return { ...scene, inlineBlockIds };
}

export function ensureScenarioBlockIds(scenario: Scenario): Scenario {
  let changed = false;
  const scenes = scenario.scenes.map((scene) => {
    const next = ensureSceneBlockIds(scene);
    if (next !== scene) changed = true;
    return next;
  });
  return changed ? { ...scenario, scenes } : scenario;
}

export function getChapterMainScenes(chapterScenes: Scene[]): Scene[] {
  return chapterScenes.filter((s) => !isBranchSceneId(s.id));
}

export function getChapterMainScene(chapterScenes: Scene[]): Scene | undefined {
  return getChapterMainScenes(chapterScenes)[0];
}

/** Toutes les scènes d'un chapitre, y compris les branches rattachées aux scènes principales. */
export function collectChapterSceneIds(scenario: Scenario, chapterScenes: Scene[]): Set<string> {
  const ids = new Set(chapterScenes.map((s) => s.id));
  const mainSceneIds = getChapterMainScenes(chapterScenes).map((s) => s.id);
  for (const scene of scenario.scenes) {
    if (ids.has(scene.id)) continue;
    for (const mainId of mainSceneIds) {
      if (scene.id.startsWith(`${mainId}--`)) {
        ids.add(scene.id);
      }
    }
  }
  return ids;
}

/** Flat script blocks for one chapter (main scene order, branch duplicates hidden). */
export function buildChapterFlatBlocks(chapterScenes: Scene[]): ChapterFlatBlock[] {
  const seen = new Set<string>();
  const blocks: ChapterFlatBlock[] = [];

  for (const scene of getChapterMainScenes(chapterScenes)) {
    const withIds = ensureSceneBlockIds(scene);
    withIds.content.forEach((item, contentIndex) => {
      const blockId = withIds.inlineBlockIds![contentIndex];
      if (seen.has(blockId)) return;
      seen.add(blockId);
      blocks.push({
        blockId,
        item,
        sceneId: withIds.id,
        contentIndex,
        flatIndex: blocks.length,
      });
    });
  }

  for (const scene of chapterScenes.filter((s) => isBranchSceneId(s.id))) {
    const withIds = ensureSceneBlockIds(scene);
    withIds.content.forEach((item, contentIndex) => {
      const blockId = withIds.inlineBlockIds![contentIndex];
      if (seen.has(blockId)) return;
      seen.add(blockId);
      blocks.push({
        blockId,
        item,
        sceneId: withIds.id,
        contentIndex,
        flatIndex: blocks.length,
      });
    });
  }

  return blocks;
}

export function listInlineBlocksInScenes(scenes: Scene[]): InlineBlockRef[] {
  return buildChapterFlatBlocks(scenes).map((b) => ({
    blockId: b.blockId,
    sceneId: b.sceneId,
    contentIndex: b.contentIndex,
    label: getContentItemLabel(b.item),
    summary: getContentItemSummary(b.item),
  }));
}

export function listLinkableBlocksBelow(
  flatBlocks: ChapterFlatBlock[],
  fromFlatIndex: number
): InlineBlockRef[] {
  return flatBlocks.slice(fromFlatIndex + 1).map((b) => ({
    blockId: b.blockId,
    sceneId: b.sceneId,
    contentIndex: b.contentIndex,
    label: getContentItemLabel(b.item),
    summary: getContentItemSummary(b.item),
  }));
}

function findBlockById(scenario: Scenario, blockId: string): InlineBlockRef | null {
  for (const scene of scenario.scenes) {
    const withIds = ensureSceneBlockIds(scene);
    const idx = withIds.inlineBlockIds?.indexOf(blockId) ?? -1;
    if (idx >= 0) {
      const item = withIds.content[idx];
      return {
        blockId,
        sceneId: withIds.id,
        contentIndex: idx,
        label: getContentItemLabel(item),
        summary: getContentItemSummary(item),
      };
    }
  }
  return null;
}

export function inferOptionBlockId(
  scenario: Scenario,
  choiceSceneId: string,
  choiceContentIndex: number,
  option: BranchingOption
): string | undefined {
  if (option.gotoBlockId) return option.gotoBlockId;

  if (option.gotoScene) {
    const targetScene = scenario.scenes.find((s) => s.id === option.gotoScene);
    if (!targetScene || targetScene.content.length === 0) return undefined;
    const targetWithIds = ensureSceneBlockIds(targetScene);
    return targetWithIds.inlineBlockIds![0];
  }

  if ('continueInScene' in option && option.continueInScene) {
    const choiceScene = scenario.scenes.find((s) => s.id === choiceSceneId);
    if (!choiceScene) return undefined;
    const withIds = ensureSceneBlockIds(choiceScene);
    const nextIdx = choiceContentIndex + 1;
    if (nextIdx < withIds.content.length) {
      return withIds.inlineBlockIds![nextIdx];
    }
  }

  return undefined;
}

function branchSceneIdForOptionLink(
  scenario: Scenario,
  sourceSceneId: string,
  optionText: string,
  optionIndex: number,
  currentGotoScene?: string
): string {
  const usedIds = new Set(scenario.scenes.map((s) => s.id));
  if (currentGotoScene) usedIds.delete(currentGotoScene);

  const keepId =
    currentGotoScene &&
    isBranchSceneId(currentGotoScene) &&
    !currentGotoScene.includes('__blk_')
      ? currentGotoScene
      : undefined;

  return branchSceneIdForOption(sourceSceneId, optionText, optionIndex, usedIds, keepId);
}

function removeSceneById(scenario: Scenario, sceneId: string): Scenario {
  return { ...scenario, scenes: scenario.scenes.filter((s) => s.id !== sceneId) };
}

function resolveMainSceneId(sceneId: string): string {
  const sep = sceneId.indexOf('--');
  return sep > 0 ? sceneId.slice(0, sep) : sceneId;
}

function removeMainSceneBlockByBlockId(
  scenario: Scenario,
  mainSceneId: string,
  blockId: string
): Scenario {
  const scene = scenario.scenes.find((s) => s.id === mainSceneId);
  if (!scene) return scenario;
  const withIds = ensureSceneBlockIds(scene);
  const idx = withIds.inlineBlockIds?.indexOf(blockId) ?? -1;
  if (idx < 0) return scenario;
  return {
    ...scenario,
    scenes: scenario.scenes.map((s) => {
      if (s.id !== mainSceneId) return s;
      return {
        ...s,
        content: s.content.filter((_, i) => i !== idx),
        inlineBlockIds: s.inlineBlockIds?.filter((_, i) => i !== idx),
      };
    }),
  };
}

function findBranchLink(
  scenario: Scenario,
  mainSceneId: string,
  branchSceneId: string,
  blockId?: string
): { choiceContentIndex: number; optionIndex: number } | null {
  const mainScene = scenario.scenes.find((s) => s.id === mainSceneId);
  if (!mainScene) return null;

  for (let ci = 0; ci < mainScene.content.length; ci++) {
    const options = getBranchingOptions(mainScene.content[ci]);
    if (!options) continue;
    for (let oi = 0; oi < options.length; oi++) {
      const opt = options[oi];
      if (opt.gotoScene === branchSceneId) {
        return { choiceContentIndex: ci, optionIndex: oi };
      }
      if (blockId && opt.gotoBlockId === blockId) {
        return { choiceContentIndex: ci, optionIndex: oi };
      }
    }
  }
  return null;
}

function hasConvergenceContent(
  mainScene: Scene,
  branchingContentIndex: number,
  branchingItem: BranchingContentItem,
  scenario: Scenario
): boolean {
  if (isPhoneReplyItem(branchingItem) && !branchingItem.replyOptions?.length) {
    const withIds = ensureSceneBlockIds(mainScene);
    for (let j = branchingContentIndex + 1; j < withIds.content.length; j++) {
      if (isChapterSegmentBoundary(withIds.content[j])) break;
      return true;
    }
    const convScene = scenario.scenes.find((s) => s.id === convergeSceneId(mainScene.id));
    return Boolean(convScene?.content.length);
  }

  const options = getBranchingOptions(branchingItem);
  if (!options) return false;

  const withIds = ensureSceneBlockIds(mainScene);
  const branchEntryIds = getBranchEntryBlockIds(
    scenario,
    mainScene.id,
    branchingContentIndex,
    options
  );
  const branchEntryIndices: number[] = [];
  withIds.inlineBlockIds!.forEach((bid, idx) => {
    if (idx > branchingContentIndex && branchEntryIds.has(bid)) branchEntryIndices.push(idx);
  });
  const poolEnd =
    branchEntryIndices.length > 0
      ? Math.max(...branchEntryIndices)
      : branchingContentIndex;

  for (let j = branchingContentIndex + 1; j < withIds.content.length; j++) {
    if (isChapterSegmentBoundary(withIds.content[j])) break;
    const bid = withIds.inlineBlockIds![j];
    if (branchEntryIds.has(bid)) continue;
    if (j > poolEnd) return true;
  }
  return false;
}

function shouldKeepConvergeScene(scenario: Scenario, mainSceneId: string): boolean {
  const mainScene = scenario.scenes.find((s) => s.id === mainSceneId);
  if (!mainScene) return false;

  const convId = convergeSceneId(mainSceneId);
  const convScene = scenario.scenes.find((s) => s.id === convId);
  const convHasContent = Boolean(convScene?.content.length);

  for (let i = 0; i < mainScene.content.length; i++) {
    const item = mainScene.content[i];
    if (isPhoneReplyItem(item)) {
      // SMS/appel avec réponses qui convergent vers convId.
      if (item.replyOptions?.length && collectPhoneReplyGotoScenes(item.replyOptions).includes(convId)) {
        return true;
      }
      // SMS/appel sans réponse qui pointe vers sa convergence.
      if (!item.replyOptions?.length && item.gotoScene === convId && convHasContent) {
        return true;
      }
      // Convergence encore inline (avant absorption par le resync).
      if (hasConvergenceContent(mainScene, i, item, scenario)) {
        return true;
      }
      continue;
    }
    if (!isInlineBranchingItem(item)) continue;
    const options = getBranchingOptions(item)!;
    const linkedBranchIds = options
      .map((o) => o.gotoScene)
      .filter(
        (id): id is string =>
          Boolean(id) &&
          isBranchSceneId(id) &&
          !isConvergeSceneId(id) &&
          scenario.scenes.some((s) => s.id === id)
      );
    // Branches inline (--slug) ou scènes externes dont la sortie mène à convId.
    if (convHasContent) {
      const exitsViaInline = linkedBranchIds.some((branchId) => {
        const bs = scenario.scenes.find((s) => s.id === branchId);
        return bs ? branchExitGotoScene(scenario, bs) === convId : false;
      });
      if (exitsViaInline) return true;
      const exitsViaExternal = options.some((opt) => {
        if (!opt.gotoScene || opt.gotoScene === convId || isConvergeSceneId(opt.gotoScene)) {
          return false;
        }
        if (isBranchSceneId(opt.gotoScene)) return false;
        const ext = scenario.scenes.find((s) => s.id === opt.gotoScene);
        return ext ? branchExitGotoScene(scenario, ext) === convId : false;
      });
      if (exitsViaExternal) return true;
    }
    // Choix plat : les options pointent directement vers convId (sans scène de branche).
    if (convHasContent && options.some((opt) => opt.gotoScene === convId)) {
      return true;
    }
    if (linkedBranchIds.length > 0 && hasConvergenceContent(mainScene, i, item, scenario)) {
      return true;
    }
  }
  return false;
}

/** Remove orphan branch / converge scenes for an inline chapter. */
export function pruneChapterInlineScenes(scenario: Scenario, mainSceneId: string): Scenario {
  const prefix = `${mainSceneId}--`;
  const mainScene = scenario.scenes.find((s) => s.id === mainSceneId);
  const convId = convergeSceneId(mainSceneId);

  const referencedBranches = new Set<string>();
  let hasBranching = false;

  if (mainScene) {
    mainScene.content.forEach((item) => {
      if (isPhoneSegmentItem(item)) {
        hasBranching = true;
        return;
      }
      const options = getBranchingOptions(item);
      if (!options) return;
      hasBranching = true;
      options.forEach((opt) => {
        if (
          opt.gotoScene &&
          isBranchSceneId(opt.gotoScene) &&
          !isConvergeSceneId(opt.gotoScene)
        ) {
          referencedBranches.add(opt.gotoScene);
        }
      });
    });
  }

  if (!hasBranching) {
    return {
      ...scenario,
      scenes: scenario.scenes.filter((s) => !s.id.startsWith(prefix)),
    };
  }

  const keepConverge = shouldKeepConvergeScene(scenario, mainSceneId);

  return {
    ...scenario,
    scenes: scenario.scenes.filter((s) => {
      if (!s.id.startsWith(prefix)) return true;
      if (s.id === convId) return keepConverge;
      if (isBranchSceneId(s.id)) return referencedBranches.has(s.id);
      return false;
    }),
  };
}

function resyncBranchingConvergenceForMainScene(
  scenario: Scenario,
  mainSceneId: string
): Scenario {
  const mainScene = scenario.scenes.find((s) => s.id === mainSceneId);
  if (!mainScene) return scenario;

  let next = scenario;
  mainScene.content.forEach((item, branchingContentIndex) => {
    if (!isInlineBranchingItem(item) && !isPhoneSegmentItem(item)) return;
    next = syncBranchingConvergence(next, mainSceneId, branchingContentIndex);
  });
  return next;
}

function patchBranchingOption(
  scenario: Scenario,
  sceneId: string,
  branchingContentIndex: number,
  optionIndex: number,
  patch: Partial<BranchingOption>
): Scenario {
  return {
    ...scenario,
    scenes: scenario.scenes.map((scene) => {
      if (scene.id !== sceneId) return scene;
      const content = scene.content.map((item, i) => {
        if (i !== branchingContentIndex) return item;
        if (item.type === 'choice') {
          const options = item.options.map((opt, oi) =>
            oi === optionIndex ? { ...opt, ...patch } : opt
          );
          return { ...item, options };
        }
        if (isPhoneReplyItem(item) && item.replyOptions) {
          const replyOptions = item.replyOptions.map((opt, oi) =>
            oi === optionIndex ? { ...opt, ...patch } : opt
          );
          return { ...item, replyOptions };
        }
        return item;
      });
      return { ...scene, content };
    }),
  };
}

function upsertBranchSceneForBlock(
  scenario: Scenario,
  branchId: string,
  sourceScene: Scene,
  blockIndex: number
): Scenario {
  const withIds = ensureSceneBlockIds(sourceScene);
  const item = withIds.content[blockIndex];
  const blockId = withIds.inlineBlockIds![blockIndex];
  const existing = scenario.scenes.find((s) => s.id === branchId);

  const branchScene: Scene = existing
    ? {
        ...existing,
        background: sourceScene.background,
        elements: sourceScene.elements,
        content: [
          item,
          ...existing.content.slice(1),
        ],
        inlineBlockIds: [
          blockId,
          ...(existing.inlineBlockIds?.slice(1) ?? []),
        ],
      }
    : {
        id: branchId,
        background: sourceScene.background,
        elements: sourceScene.elements,
        content: [item],
        inlineBlockIds: [blockId],
      };

  const existingIdx = scenario.scenes.findIndex((s) => s.id === branchId);
  if (existingIdx >= 0) {
    return {
      ...scenario,
      scenes: scenario.scenes.map((s, i) => (i === existingIdx ? branchScene : s)),
    };
  }
  return { ...scenario, scenes: [...scenario.scenes, branchScene] };
}

function resolveEngineTarget(
  scenario: Scenario,
  sceneId: string,
  branchingContentIndex: number,
  optionIndex: number,
  blockId: string | undefined
): Partial<ChoiceOption> {
  if (!blockId) {
    return { gotoBlockId: undefined, gotoScene: undefined, continueInScene: undefined };
  }

  const sourceScene = scenario.scenes.find((s) => s.id === sceneId);
  const branchingItem = sourceScene?.content[branchingContentIndex];
  const options = getBranchingOptions(branchingItem);
  if (!options) {
    return { gotoBlockId: blockId };
  }
  const option = options[optionIndex];

  const target = findBlockById(scenario, blockId);
  if (!target) {
    return { gotoBlockId: blockId };
  }

  if (target.sceneId !== sceneId || target.contentIndex <= branchingContentIndex) {
    if (isBranchSceneId(target.sceneId)) {
      return {
        gotoBlockId: blockId,
        gotoScene: option.gotoScene === target.sceneId ? option.gotoScene : target.sceneId,
      };
    }
    return { gotoBlockId: blockId };
  }

  const branchId = branchSceneIdForOptionLink(
    scenario,
    sceneId,
    option.text,
    optionIndex,
    option.gotoScene
  );

  return {
    gotoBlockId: blockId,
    gotoScene: branchId,
    continueInScene: undefined,
  };
}

function syncBranchSceneForOption(
  scenario: Scenario,
  sceneId: string,
  branchingContentIndex: number,
  optionIndex: number,
  blockId: string | undefined
): Scenario {
  let next = ensureScenarioBlockIds(scenario);

  const sourceScene = next.scenes.find((s) => s.id === sceneId);
  const branchingItem = sourceScene?.content[branchingContentIndex];
  const options = getBranchingOptions(branchingItem);
  const previousGotoScene = options?.[optionIndex]?.gotoScene;

  const enginePatch = resolveEngineTarget(next, sceneId, branchingContentIndex, optionIndex, blockId);
  const nextGotoScene =
    enginePatch.gotoScene !== undefined ? enginePatch.gotoScene : previousGotoScene;

  if (enginePatch.gotoScene && blockId) {
    const target = findBlockById(next, blockId);
    const existingBranch = next.scenes.find((s) => s.id === enginePatch.gotoScene);
    const needsUpsert =
      !existingBranch ||
      existingBranch.content.length === 0 ||
      existingBranch.inlineBlockIds?.[0] !== blockId;

    if (needsUpsert && target) {
      const sceneForBlock = next.scenes.find((s) => s.id === target.sceneId);
      if (sceneForBlock) {
        next = upsertBranchSceneForBlock(
          next,
          enginePatch.gotoScene,
          sceneForBlock,
          target.contentIndex
        );
      }
    }
  }

  if (
    previousGotoScene &&
    nextGotoScene !== previousGotoScene &&
    isBranchSceneId(previousGotoScene)
  ) {
    next = removeSceneById(next, previousGotoScene);
  }

  return patchBranchingOption(next, sceneId, branchingContentIndex, optionIndex, enginePatch);
}

/** Set a choice option destination by inline block id; always creates a branch scene. */
export function applyChoiceBlockLink(
  scenario: Scenario,
  sceneId: string,
  choiceContentIndex: number,
  optionIndex: number,
  blockId: string | undefined
): Scenario {
  const next = syncBranchSceneForOption(
    scenario,
    sceneId,
    choiceContentIndex,
    optionIndex,
    blockId
  );
  return resyncChoiceBranches(next, sceneId);
}

/** Id de la scène de convergence auto-générée pour un chapitre inline. */
export function convergeSceneId(mainSceneId: string): string {
  return `${mainSceneId}--converge`;
}

export function isConvergeSceneId(sceneId: string): boolean {
  return sceneId.endsWith('--converge');
}

export interface ChoiceBranchLayout {
  optionIndex: number;
  branchSceneId: string | undefined;
  blocks: ChapterFlatBlock[];
}

export type ChapterScriptSegment =
  | { kind: 'block'; block: ChapterFlatBlock }
  | {
      kind: 'choice';
      block: ChapterFlatBlock;
      branches: ChoiceBranchLayout[];
      pool: ChapterFlatBlock[];
      convergence: ChapterFlatBlock[];
    }
  | {
      kind: 'phoneCall' | 'phoneChat';
      block: ChapterFlatBlock;
      branches: ChoiceBranchLayout[];
      pool: ChapterFlatBlock[];
      convergence: ChapterFlatBlock[];
    };

function toFlatBlock(
  scene: Scene,
  contentIndex: number,
  flatIndex: number
): ChapterFlatBlock {
  const withIds = ensureSceneBlockIds(scene);
  return {
    blockId: withIds.inlineBlockIds![contentIndex],
    item: withIds.content[contentIndex],
    sceneId: withIds.id,
    contentIndex,
    flatIndex,
  };
}

export function branchBlocksFromScene(
  scenario: Scenario,
  branchSceneId: string | undefined,
  startFlatIndex: number
): ChapterFlatBlock[] {
  if (!branchSceneId) return [];
  const scene = scenario.scenes.find((s) => s.id === branchSceneId);
  if (!scene) return [];
  const withIds = ensureSceneBlockIds(scene);
  return withIds.content.map((_, i) => toFlatBlock(withIds, i, startFlatIndex + i));
}

export function getBranchEntryBlockIds(
  scenario: Scenario,
  sceneId: string,
  branchingContentIndex: number,
  options: BranchingOption[]
): Set<string> {
  const ids = new Set<string>();
  options.forEach((opt) => {
    const blockId = inferOptionBlockId(scenario, sceneId, branchingContentIndex, opt);
    if (blockId) ids.add(blockId);
  });
  return ids;
}

/** @deprecated Use getBranchEntryBlockIds */
export function getChoiceBranchEntryBlockIds(
  scenario: Scenario,
  choiceSceneId: string,
  choiceContentIndex: number,
  choiceItem: ChoiceItem
): Set<string> {
  return getBranchEntryBlockIds(
    scenario,
    choiceSceneId,
    choiceContentIndex,
    choiceItem.options
  );
}

function branchExitGotoFromContent(scenario: Scenario, scene: Scene, visited = new Set<string>()): string | undefined {
  if (visited.has(scene.id)) return undefined;
  visited.add(scene.id);
  const idx = lastBranchLineIndex(scene.content);
  if (idx >= 0) {
    const item = scene.content[idx];
    if (item.type === 'dialogue' || item.type === 'phoneCallBranchLine') {
      if (item.gotoScene) return item.gotoScene;
    }
  }

  for (let i = scene.content.length - 1; i >= 0; i--) {
    const item = scene.content[i];
    const options = getBranchingOptions(item);
    if (!options?.length) continue;

    const exits = options.map((opt) => {
      if (!opt.gotoScene) return undefined;
      const branchScene = scenario.scenes.find((s) => s.id === opt.gotoScene);
      if (branchScene) return branchExitGotoFromContent(scenario, branchScene, visited);
      return opt.gotoScene;
    });

    if (exits.some((t) => !t)) continue;
    const first = exits[0]!;
    if (exits.every((t) => t === first)) return first;
  }

  return undefined;
}

function branchExitGotoScene(scenario: Scenario, scene: Scene): string | undefined {
  return branchExitGotoFromContent(scenario, scene);
}

function isSharedGotoTarget(options: BranchingOption[], gotoScene: string): boolean {
  return options.filter((o) => o.gotoScene === gotoScene).length > 1;
}

/** Branch scene for a choice option — inline (`scene--slug`) or external (`s22_piano`). */
function resolveOptionBranchSceneId(
  scenario: Scenario,
  option: BranchingOption,
  options: BranchingOption[]
): string | undefined {
  if (!option.gotoScene || isConvergeSceneId(option.gotoScene)) return undefined;
  if (isSharedGotoTarget(options, option.gotoScene)) return undefined;
  if (isBranchSceneId(option.gotoScene)) return option.gotoScene;

  const scene = scenario.scenes.find((s) => s.id === option.gotoScene);
  if (!scene || scene.content.length === 0) return undefined;

  if (option.gotoBlockId) {
    const blockRef = findBlockById(scenario, option.gotoBlockId);
    // gotoBlockId marks the entry block inside the external branch scene (after resync)
    if (blockRef?.sceneId === option.gotoScene) return option.gotoScene;
    // Inline pool block in the choice scene — not an external branch
    if (blockRef && blockRef.sceneId !== option.gotoScene && !isBranchSceneId(blockRef.sceneId)) {
      return undefined;
    }
  }

  return option.gotoScene;
}

function findExternalConvergeSceneId(
  scenario: Scenario,
  branchSceneIds: Array<string | undefined>
): string | undefined {
  const ids = branchSceneIds.filter((id): id is string => Boolean(id));
  if (ids.length === 0) return undefined;

  const targets = ids.map((id) => {
    const scene = scenario.scenes.find((s) => s.id === id);
    return scene ? branchExitGotoScene(scenario, scene) : undefined;
  });

  if (targets.some((t) => !t)) return undefined;
  const first = targets[0]!;
  if (!targets.every((t) => t === first)) return undefined;
  return first;
}

interface LayoutBuildState {
  absorbedSceneIds: Set<string>;
  consumedContentKeys: Set<string>;
}

function layoutContentKey(sceneId: string, contentIndex: number): string {
  return `${sceneId}:${contentIndex}`;
}

function markLayoutContentConsumed(
  state: LayoutBuildState,
  sceneId: string,
  contentIndex: number
): void {
  state.consumedContentKeys.add(layoutContentKey(sceneId, contentIndex));
}

function isLayoutContentConsumed(
  state: LayoutBuildState,
  sceneId: string,
  contentIndex: number
): boolean {
  return state.consumedContentKeys.has(layoutContentKey(sceneId, contentIndex));
}

function buildBranchingSegment(
  scenario: Scenario,
  withIds: Scene,
  i: number,
  item: BranchingContentItem,
  kind: 'choice' | PhoneReplySegmentKind,
  flatCounter: number,
  layoutState: LayoutBuildState
): { segment: ChapterScriptSegment; flatCounter: number; skip: number } {
  const options = getBranchingOptions(item)!;
  const branchEntryIds = getBranchEntryBlockIds(scenario, withIds.id, i, options);
  const branchEntryIndices: number[] = [];
  withIds.inlineBlockIds!.forEach((bid, idx) => {
    if (idx > i && branchEntryIds.has(bid)) branchEntryIndices.push(idx);
  });
  const poolEnd =
    branchEntryIndices.length > 0 ? Math.max(...branchEntryIndices) : i;

  const useExternalChoiceBranches = item.type === 'choice';

  const branches: ChoiceBranchLayout[] = options.map((opt, optionIndex) => {
    const branchSceneId = useExternalChoiceBranches
      ? resolveOptionBranchSceneId(scenario, opt, options)
      : opt.gotoScene && isBranchSceneId(opt.gotoScene) && !isConvergeSceneId(opt.gotoScene)
        ? opt.gotoScene
        : undefined;

    if (branchSceneId && !isBranchSceneId(branchSceneId)) {
      layoutState.absorbedSceneIds.add(branchSceneId);
    }

    return {
      optionIndex,
      branchSceneId,
      blocks: branchBlocksFromScene(scenario, branchSceneId, flatCounter),
    };
  });
  let counter = flatCounter + branches.reduce((n, b) => n + b.blocks.length, 0);

  const pool: ChapterFlatBlock[] = [];
  const convergence: ChapterFlatBlock[] = [];
  for (let j = i + 1; j < withIds.content.length; j++) {
    if (isChapterSegmentBoundary(withIds.content[j])) break;
    const bid = withIds.inlineBlockIds![j];
    if (branchEntryIds.has(bid)) continue;
    const block = toFlatBlock(withIds, j, counter++);
    if (j <= poolEnd) pool.push(block);
    else convergence.push(block);
  }

  if (useExternalChoiceBranches && convergence.length === 0) {
    // La convergence d'un choix isolé vit dans sa scène `--converge` dédiée. À défaut, on
    // conserve l'ancien comportement : la convergence externe partagée n'est rattachée au
    // choix que si les branches ne sont pas toutes des scènes inline (`--`).
    const ownConvId = convergeSceneId(withIds.id);
    let externalConvergeId: string | undefined;
    if (scenario.scenes.some((s) => s.id === ownConvId)) {
      externalConvergeId = ownConvId;
    } else {
      const allInlineBranches =
        branches.length > 0 &&
        branches.every(
          (b) =>
            b.branchSceneId && isBranchSceneId(b.branchSceneId) && !isConvergeSceneId(b.branchSceneId)
        );
      if (!allInlineBranches) {
        externalConvergeId = findExternalConvergeSceneId(
          scenario,
          branches.map((b) => b.branchSceneId)
        );
      }
    }
    if (externalConvergeId) {
      const convScene = scenario.scenes.find((s) => s.id === externalConvergeId);
      if (convScene) {
        const convWithIds = ensureSceneBlockIds(convScene);
        for (let j = 0; j < convWithIds.content.length; j++) {
          if (isChapterSegmentBoundary(convWithIds.content[j])) break;
          convergence.push(toFlatBlock(convWithIds, j, counter++));
          markLayoutContentConsumed(layoutState, convWithIds.id, j);
        }
        if (isConvergeSceneId(externalConvergeId)) {
          layoutState.absorbedSceneIds.add(externalConvergeId);
        }
      }
    }
  }

  if (
    !useExternalChoiceBranches &&
    convergence.length === 0 &&
    isPhoneReplyItem(item) &&
    item.replyOptions
  ) {
    const convId = convergeSceneId(withIds.id);
    if (collectPhoneReplyGotoScenes(item.replyOptions).includes(convId)) {
      counter = appendConvergeSceneToLayout(
        scenario,
        convId,
        convergence,
        counter,
        layoutState
      );
    }
  }

  const segment: ChapterScriptSegment = {
    kind,
    block: toFlatBlock(withIds, i, counter++),
    branches,
    pool,
    convergence,
  };

  return {
    segment,
    flatCounter: counter,
    skip: branchEntryIndices.length + pool.length + convergence.length,
  };
}

function buildPhoneSegment(
  scenario: Scenario,
  withIds: Scene,
  i: number,
  item: PhoneReplyBranchItem,
  flatCounter: number,
  layoutState: LayoutBuildState
): { segment: ChapterScriptSegment; flatCounter: number; skip: number } {
  const kind = phoneReplySegmentKind(item);
  let counter = flatCounter;

  const convergence: ChapterFlatBlock[] = [];
  for (let j = i + 1; j < withIds.content.length; j++) {
    if (isChapterSegmentBoundary(withIds.content[j])) break;
    convergence.push(toFlatBlock(withIds, j, counter++));
  }

  const convId = convergeSceneId(withIds.id);
  const referencesConverge = item.replyOptions?.length
    ? collectPhoneReplyGotoScenes(item.replyOptions).includes(convId)
    : item.gotoScene === convId;
  if (convergence.length === 0 && referencesConverge) {
    counter = appendConvergeSceneToLayout(
      scenario,
      convId,
      convergence,
      counter,
      layoutState
    );
  }

  const segment: ChapterScriptSegment = {
    kind,
    block: toFlatBlock(withIds, i, counter++),
    branches: [],
    pool: [],
    convergence,
  };

  return {
    segment,
    flatCounter: counter,
    skip: convergence.length,
  };
}

/** Choice segment layout for a choice nested inside a branch (or any scene). */
export function buildNestedChoiceSegment(
  scenario: Scenario,
  choiceBlock: ChapterFlatBlock
): Extract<ChapterScriptSegment, { kind: 'choice' }> | null {
  if (choiceBlock.item.type !== 'choice') return null;
  const scene = scenario.scenes.find((s) => s.id === choiceBlock.sceneId);
  if (!scene) return null;

  const withIds = ensureSceneBlockIds(scene);
  const layoutState: LayoutBuildState = {
    absorbedSceneIds: new Set<string>(),
    consumedContentKeys: new Set<string>(),
  };
  const { segment } = buildBranchingSegment(
    scenario,
    withIds,
    choiceBlock.contentIndex,
    choiceBlock.item,
    'choice',
    choiceBlock.flatIndex,
    layoutState
  );
  return segment.kind === 'choice' ? segment : null;
}

/** Structured script layout: branches nested under choices, convergence after branches. */
export function buildChapterScriptLayout(
  scenario: Scenario,
  chapterScenes: Scene[]
): ChapterScriptSegment[] {
  const mainScenes = getChapterMainScenes(chapterScenes);
  if (mainScenes.length === 0) return [];

  const segments: ChapterScriptSegment[] = [];
  let flatCounter = 0;
  const layoutState: LayoutBuildState = {
    absorbedSceneIds: new Set<string>(),
    consumedContentKeys: new Set<string>(),
  };

  for (const mainScene of mainScenes) {
    if (layoutState.absorbedSceneIds.has(mainScene.id)) continue;

    const withIds = ensureSceneBlockIds(mainScene);

    for (let i = 0; i < withIds.content.length; i++) {
      if (isLayoutContentConsumed(layoutState, withIds.id, i)) continue;

      const item = withIds.content[i];

      if (isPhoneSegmentItem(item)) {
        const { segment, flatCounter: nextCounter, skip } = buildPhoneSegment(
          scenario,
          withIds,
          i,
          item,
          flatCounter,
          layoutState
        );
        segments.push(segment);
        flatCounter = nextCounter;
        i += skip;
        continue;
      }

      if (isInlineBranchingItem(item)) {
        const kind =
          item.type === 'choice' ? 'choice' : phoneReplySegmentKind(item);
        const { segment, flatCounter: nextCounter, skip } = buildBranchingSegment(
          scenario,
          withIds,
          i,
          item,
          kind,
          flatCounter,
          layoutState
        );
        segments.push(segment);
        flatCounter = nextCounter;
        i += skip;
        continue;
      }

      segments.push({
        kind: 'block',
        block: toFlatBlock(withIds, i, flatCounter++),
      });
    }
  }

  return segments;
}

function lastBranchLineIndex(content: ContentItem[]): number {
  for (let i = content.length - 1; i >= 0; i--) {
    if (content[i].type === 'dialogue' || content[i].type === 'phoneCallBranchLine') return i;
  }
  return -1;
}

function patchBranchSceneGoto(scenario: Scenario, branchSceneId: string, gotoScene?: string): Scenario {
  const scene = scenario.scenes.find((s) => s.id === branchSceneId);
  if (!scene || scene.content.length === 0) return scenario;

  const idx = lastBranchLineIndex(scene.content);
  if (idx < 0) return scenario;

  return {
    ...scenario,
    scenes: scenario.scenes.map((s) => {
      if (s.id !== branchSceneId) return s;
      const content = s.content.map((item, i) => {
        if (i !== idx) return item;
        if (item.type === 'dialogue') {
          return gotoScene ? { ...item, gotoScene } : { ...item, gotoScene: undefined };
        }
        if (item.type === 'phoneCallBranchLine') {
          return gotoScene ? { ...item, gotoScene } : { ...item, gotoScene: undefined };
        }
        return item;
      });
      return { ...s, content };
    }),
  };
}

function appendConvergeSceneToLayout(
  scenario: Scenario,
  convSceneId: string,
  convergence: ChapterFlatBlock[],
  flatCounter: number,
  layoutState: LayoutBuildState
): number {
  const convScene = scenario.scenes.find((s) => s.id === convSceneId);
  if (!convScene) return flatCounter;

  let counter = flatCounter;
  const convWithIds = ensureSceneBlockIds(convScene);
  for (let j = 0; j < convWithIds.content.length; j++) {
    if (isChapterSegmentBoundary(convWithIds.content[j])) break;
    convergence.push(toFlatBlock(convWithIds, j, counter++));
    markLayoutContentConsumed(layoutState, convWithIds.id, j);
  }
  layoutState.absorbedSceneIds.add(convSceneId);
  return counter;
}

/** Ajoute des blocs à la fin de la scène de convergence (en préservant le contenu existant). */
function appendItemsToConvergeScene(
  scenario: Scenario,
  mainScene: Scene,
  convId: string,
  newItems: ContentItem[],
  newIds: string[]
): Scenario {
  const existing = scenario.scenes.find((s) => s.id === convId);
  const existingWithIds = existing ? ensureSceneBlockIds(existing) : undefined;
  const baseContent = existingWithIds ? [...existingWithIds.content] : [];
  const baseIds = existingWithIds ? [...existingWithIds.inlineBlockIds!] : [];

  // Le lien de sortie ne doit rester que sur le dernier bloc : on le retire de l'ancien dernier.
  if (baseContent.length > 0 && newItems.length > 0) {
    const li = baseContent.length - 1;
    if (exitGotoOf(baseContent[li]!) !== undefined) {
      baseContent[li] = clearExitGoto(baseContent[li]!);
    }
  }

  const content = [...baseContent, ...newItems];
  const inlineBlockIds = syncBlockIdsForContent([...baseIds, ...newIds], content.length);
  const convergeScene: Scene = {
    id: convId,
    background: mainScene.background,
    elements: mainScene.elements,
    content,
    inlineBlockIds,
  };

  const idx = scenario.scenes.findIndex((s) => s.id === convId);
  if (idx >= 0) {
    return { ...scenario, scenes: scenario.scenes.map((s, i) => (i === idx ? convergeScene : s)) };
  }
  return { ...scenario, scenes: [...scenario.scenes, convergeScene] };
}

function stripSceneContentAt(scenario: Scenario, sceneId: string, indices: number[]): Scenario {
  if (indices.length === 0) return scenario;
  const set = new Set(indices);
  return {
    ...scenario,
    scenes: scenario.scenes.map((s) => {
      if (s.id !== sceneId) return s;
      const withIds = ensureSceneBlockIds(s);
      return {
        ...s,
        content: withIds.content.filter((_, i) => !set.has(i)),
        inlineBlockIds: withIds.inlineBlockIds!.filter((_, i) => !set.has(i)),
      };
    }),
  };
}

/** Modifie le `gotoScene` de tête d'un bloc SMS/appel sans réponses dans la scène principale. */
function patchReplylessPhoneGoto(
  scenario: Scenario,
  mainSceneId: string,
  index: number,
  gotoScene: string | undefined
): Scenario {
  return {
    ...scenario,
    scenes: scenario.scenes.map((s) => {
      if (s.id !== mainSceneId) return s;
      const content = s.content.map((item, i) => {
        if (i !== index) return item;
        if ((item.type === 'phoneChat' || item.type === 'phoneCall') && !item.replyOptions?.length) {
          return { ...item, gotoScene };
        }
        return item;
      });
      return { ...s, content };
    }),
  };
}

/**
 * Synchronise la convergence d'un bloc branchant.
 *
 * Règle : la scène branchante ne contient QUE le bloc branchant. Tout contenu narratif
 * ajouté à la suite (inline) est déplacé dans la scène externe `--converge`, puis retiré
 * de la scène branchante. Les liens `gotoScene` sont posés pour que le moteur atteigne
 * bien la convergence (SMS/appel sans réponse → convId ; branches de choix → convId).
 */
function syncBranchingConvergence(
  scenario: Scenario,
  mainSceneId: string,
  branchingContentIndex: number
): Scenario {
  const mainScene = scenario.scenes.find((s) => s.id === mainSceneId);
  if (!mainScene) return scenario;

  const withIds = ensureSceneBlockIds(mainScene);
  const branchingItem = withIds.content[branchingContentIndex];
  if (!branchingItem) return scenario;
  const convId = convergeSceneId(mainSceneId);

  const options = getBranchingOptions(branchingItem);
  const branchEntryIds = options
    ? getBranchEntryBlockIds(scenario, mainSceneId, branchingContentIndex, options)
    : new Set<string>();
  const branchEntryIndices: number[] = [];
  withIds.inlineBlockIds!.forEach((bid, idx) => {
    if (idx > branchingContentIndex && branchEntryIds.has(bid)) branchEntryIndices.push(idx);
  });
  const poolEnd =
    branchEntryIndices.length > 0 ? Math.max(...branchEntryIndices) : branchingContentIndex;

  const linkedBranchIds = options
    ? options
        .map((o) => o.gotoScene)
        .filter(
          (id): id is string => Boolean(id) && isBranchSceneId(id) && !isConvergeSceneId(id)
        )
    : [];

  // Ids des blocs d'entrée de branche déjà recopiés dans une scène de branche : on peut
  // les retirer de la scène du choix en toute sécurité.
  const mirroredEntryIds = new Set<string>();
  for (const branchId of linkedBranchIds) {
    const bs = scenario.scenes.find((s) => s.id === branchId);
    const firstId = bs ? ensureSceneBlockIds(bs).inlineBlockIds?.[0] : undefined;
    if (firstId) mirroredEntryIds.add(firstId);
  }

  // Contenu de convergence resté inline (à déplacer dans --converge) + pool déjà externalisé
  // (à simplement retirer) : la scène branchante ne doit garder que le bloc branchant.
  const convItems: ContentItem[] = [];
  const convIds: string[] = [];
  const stripIndices: number[] = [];
  for (let j = branchingContentIndex + 1; j < withIds.content.length; j++) {
    if (isChapterSegmentBoundary(withIds.content[j])) break;
    const bid = withIds.inlineBlockIds![j];
    if (branchEntryIds.has(bid)) {
      if (mirroredEntryIds.has(bid)) stripIndices.push(j);
      continue;
    }
    if (j > poolEnd) {
      convItems.push(withIds.content[j]);
      convIds.push(bid);
      stripIndices.push(j);
    }
  }
  const replies = isPhoneReplyItem(branchingItem) ? branchingItem.replyOptions : undefined;
  const referencesConverge = replies?.length
    ? collectPhoneReplyGotoScenes(replies).includes(convId)
    : false;

  let next: Scenario = scenario;

  // 1. Déplacer la convergence inline vers la scène externe, puis isoler la scène branchante
  //    (on retire aussi le pool déjà recopié dans les scènes de branche).
  if (convItems.length > 0) {
    next = appendItemsToConvergeScene(next, withIds, convId, convItems, convIds);
  }
  if (stripIndices.length > 0) {
    next = stripSceneContentAt(next, mainSceneId, stripIndices);
  }

  const convScene = next.scenes.find((s) => s.id === convId);
  const convHasContent = !!convScene && convScene.content.length > 0;
  const needsConverge = convHasContent || referencesConverge;

  if (!needsConverge) {
    next = removeSceneById(next, convId);
    for (const branchId of linkedBranchIds) {
      const bs = next.scenes.find((s) => s.id === branchId);
      if (bs && branchExitGotoScene(next, bs) === convId) {
        next = patchBranchSceneGoto(next, branchId, undefined);
      }
    }
    if (isPhoneReplyItem(branchingItem) && !replies?.length && branchingItem.gotoScene === convId) {
      next = patchReplylessPhoneGoto(next, mainSceneId, branchingContentIndex, undefined);
    }
    return next;
  }

  // 2. Câbler les liens vers la convergence.
  for (const branchId of linkedBranchIds) {
    next = patchBranchSceneGoto(next, branchId, convId);
  }
  if (isPhoneReplyItem(branchingItem) && !replies?.length) {
    next = patchReplylessPhoneGoto(next, mainSceneId, branchingContentIndex, convId);
  }
  return next;
}

/** Re-sync branch scenes after content edits (choice options and phone call replies with gotoBlockId). */
export function resyncChoiceBranches(scenario: Scenario, sceneId: string): Scenario {
  const scene = scenario.scenes.find((s) => s.id === sceneId);
  if (!scene) return scenario;

  let next = scenario;
  scene.content.forEach((item, branchingContentIndex) => {
    if (item.type === 'choice') {
      item.options.forEach((option, optionIndex) => {
        if (
          option.gotoScene &&
          !isBranchSceneId(option.gotoScene) &&
          !isConvergeSceneId(option.gotoScene) &&
          scenario.scenes.some((s) => s.id === option.gotoScene)
        ) {
          return;
        }
        const blockId = inferOptionBlockId(next, sceneId, branchingContentIndex, option);
        if (!blockId) return;
        next = syncBranchSceneForOption(
          next,
          sceneId,
          branchingContentIndex,
          optionIndex,
          blockId
        );
      });
    }
    if (isInlineBranchingItem(item) || isPhoneSegmentItem(item)) {
      next = syncBranchingConvergence(next, sceneId, branchingContentIndex);
    }
  });
  next = pruneChapterInlineScenes(next, sceneId);
  return next;
}

export function blockRefLabel(ref: InlineBlockRef): string {
  return ref.summary ? `${ref.label} — ${ref.summary}` : ref.label;
}

export function blockLabelForOption(
  scenario: Scenario,
  flatBlocks: ChapterFlatBlock[],
  choiceSceneId: string,
  choiceContentIndex: number,
  option: BranchingOption
): string {
  const blockId = inferOptionBlockId(scenario, choiceSceneId, choiceContentIndex, option);
  if (!blockId) return '';
  const ref = flatBlocks.find((b) => b.blockId === blockId);
  if (!ref) return '';
  return blockRefLabel({
    blockId: ref.blockId,
    sceneId: ref.sceneId,
    contentIndex: ref.contentIndex,
    label: getContentItemLabel(ref.item),
    summary: getContentItemSummary(ref.item),
  });
}

export function updateFlatBlockItem(
  scenario: Scenario,
  block: ChapterFlatBlock,
  item: ContentItem
): Scenario {
  let normalizedItem = item;
  if (isPhoneReplyItem(item) && item.replyOptions?.length) {
    const convId = convergeSceneId(block.sceneId);
    normalizedItem = {
      ...item,
      replyOptions: applyAutoConvergeToPhoneReplies(item.replyOptions, convId),
    };
  }

  let next: Scenario = {
    ...scenario,
    scenes: scenario.scenes.map((scene) => {
      if (scene.id !== block.sceneId) return scene;
      const content = [...scene.content];
      content[block.contentIndex] = normalizedItem;
      return { ...scene, content };
    }),
  };
  if (isInlineBranchingItem(normalizedItem) || isPhoneSegmentItem(normalizedItem)) {
    next = resyncChoiceBranches(next, block.sceneId);
  }
  return next;
}

/** Une scène « ancre » (départ de scénario / mission / chapitre) ne doit jamais être supprimée. */
function isAnchorScene(scenario: Scenario, sceneId: string): boolean {
  const m = scenario.metadata;
  if (m.startScene === sceneId) return true;
  if (m.chapters?.some((c) => c.startScene === sceneId)) return true;
  if (isMissionStartScene(scenario as import('../../types').Scenario, sceneId)) return true;
  return false;
}

/** Cible vers laquelle une scène enchaînait (sa convergence ou sa dernière sortie). */
function getSceneExitTarget(scenario: Scenario, sceneId: string): string | undefined {
  const conv = scenario.scenes.find((s) => s.id === convergeSceneId(sceneId));
  if (conv) {
    const t = branchExitGotoScene(scenario, conv);
    if (t) return t;
  }
  const scene = scenario.scenes.find((s) => s.id === sceneId);
  if (scene) {
    const last = scene.content[scene.content.length - 1];
    if (last) {
      const direct = exitGotoOf(last);
      if (direct) return direct;
    }
    const t = branchExitGotoScene(scenario, scene);
    if (t && t !== convergeSceneId(sceneId)) return t;
  }
  return undefined;
}

function redirectReplyOptionRefs(
  options: PhoneChatReplyOption[],
  from: string,
  to?: string
): PhoneChatReplyOption[] {
  return options.map((opt) => ({
    ...opt,
    gotoScene: opt.gotoScene === from ? to : opt.gotoScene,
    replyOptions: opt.replyOptions?.length
      ? redirectReplyOptionRefs(opt.replyOptions, from, to)
      : opt.replyOptions,
  }));
}

/** Redirige toutes les références `gotoScene === from` vers `to` (ou les efface si `to` absent). */
function redirectGotoSceneRefs(scenario: Scenario, from: string, to?: string): Scenario {
  return {
    ...scenario,
    scenes: scenario.scenes.map((scene) => ({
      ...scene,
      content: scene.content.map((item): ContentItem => {
        switch (item.type) {
          case 'dialogue':
          case 'timedOverlay':
          case 'phoneCallBranchLine':
            return item.gotoScene === from ? { ...item, gotoScene: to } : item;
          case 'phoneChat':
          case 'phoneCall': {
            let next = item.gotoScene === from ? { ...item, gotoScene: to } : item;
            if (next.replyOptions?.length) {
              next = { ...next, replyOptions: redirectReplyOptionRefs(next.replyOptions, from, to) };
            }
            return next;
          }
          case 'choice':
            return {
              ...item,
              options: item.options.map((o) =>
                o.gotoScene === from ? { ...o, gotoScene: to } : o
              ),
            };
          default:
            return item;
        }
      }),
    })),
  };
}

/** Supprime une scène dédiée devenue vide (hors ancre) et répare les liens entrants. */
function cleanupEmptyMainScene(
  scenario: Scenario,
  sceneId: string,
  onward: string | undefined
): Scenario {
  if (isBranchSceneId(sceneId)) return scenario;
  const scene = scenario.scenes.find((s) => s.id === sceneId);
  if (!scene || scene.content.length > 0) return scenario;
  if (isAnchorScene(scenario, sceneId)) return scenario;

  let next = redirectGotoSceneRefs(scenario, sceneId, onward);
  const prefix = `${sceneId}--`;
  next = {
    ...next,
    scenes: next.scenes.filter((s) => s.id !== sceneId && !s.id.startsWith(prefix)),
  };
  return next;
}

export function removeFlatBlock(scenario: Scenario, block: ChapterFlatBlock): Scenario {
  const mainSceneId = isBranchSceneId(block.sceneId)
    ? resolveMainSceneId(block.sceneId)
    : block.sceneId;

  if (isBranchSceneId(block.sceneId) && !isConvergeSceneId(block.sceneId)) {
    return removeBranchBlock(scenario, block);
  }

  // Si l'on vide une scène dédiée (bloc branchant isolé), on mémorise sa suite pour rechaîner.
  const sceneBeforeRemoval = scenario.scenes.find((s) => s.id === block.sceneId);
  const willEmptyDedicatedScene =
    !isBranchSceneId(block.sceneId) &&
    !!sceneBeforeRemoval &&
    sceneBeforeRemoval.content.length === 1;
  const onwardTarget = willEmptyDedicatedScene
    ? getSceneExitTarget(scenario, block.sceneId)
    : undefined;

  // Lien de sortie porté par le bloc supprimé : on doit le préserver pour ne pas casser la suite.
  const removedExit = sceneBeforeRemoval
    ? exitGotoOf(sceneBeforeRemoval.content[block.contentIndex]!)
    : undefined;

  const removedBlockId = ensureSceneBlockIds(
    scenario.scenes.find((s) => s.id === block.sceneId)!
  ).inlineBlockIds?.[block.contentIndex];

  let next: Scenario = {
    ...scenario,
    scenes: scenario.scenes.map((scene) => {
      if (scene.id !== block.sceneId) return scene;
      return {
        ...scene,
        content: scene.content.filter((_, i) => i !== block.contentIndex),
        inlineBlockIds: scene.inlineBlockIds?.filter((_, i) => i !== block.contentIndex),
      };
    }),
  };

  if (removedBlockId && block.sceneId === mainSceneId) {
    const mainScene = next.scenes.find((s) => s.id === mainSceneId);
    mainScene?.content.forEach((item, branchingContentIndex) => {
      const options = getBranchingOptions(item);
      if (!options) return;
      options.forEach((opt, optionIndex) => {
        if (opt.gotoBlockId !== removedBlockId) return;
        next = patchBranchingOption(next, mainSceneId, branchingContentIndex, optionIndex, {
          gotoBlockId: undefined,
          gotoScene: undefined,
          continueInScene: undefined,
        });
        if (opt.gotoScene && isBranchSceneId(opt.gotoScene)) {
          next = removeSceneById(next, opt.gotoScene);
        }
      });
    });
  }

  // Convergence vidée par la suppression de son dernier bloc porteur du lien de sortie :
  // on reporte ce lien sur les éléments qui référençaient la convergence (le SMS/appel/choix
  // précédent, ses réponses ou ses branches), puis on retire la scène vide — pour que la
  // suite (ex. l'élément d'après) reste atteignable.
  if (isConvergeSceneId(block.sceneId) && removedExit !== undefined) {
    const convNow = next.scenes.find((s) => s.id === block.sceneId);
    if (!convNow || convNow.content.length === 0) {
      next = redirectGotoSceneRefs(next, block.sceneId, removedExit);
      next = removeSceneById(next, block.sceneId);
    }
  }

  // Le lien de sortie doit rester sur le dernier bloc de la scène : si le bloc supprimé le
  // portait et qu'il reste du contenu, on le reporte sur le nouveau dernier bloc (dialogue
  // précédent), pour continuer malgré tout. Si la scène est vidée, le cas est traité plus bas.
  if (removedExit !== undefined) {
    const sceneNow = next.scenes.find((s) => s.id === block.sceneId);
    if (sceneNow && sceneNow.content.length > 0) {
      const li = sceneNow.content.length - 1;
      const last = sceneNow.content[li]!;
      if (exitGotoOf(last) === undefined) {
        const relinked = withForwardGotoScene(last, removedExit);
        if (relinked) {
          next = patchSceneFields(next, block.sceneId, {
            content: sceneNow.content.map((it, i) => (i === li ? relinked : it)),
          });
        } else {
          const withIds = ensureSceneBlockIds(sceneNow);
          const content = [...withIds.content, internalLinkerDialogue(removedExit)];
          const inlineBlockIds = syncBlockIdsForContent(withIds.inlineBlockIds, content.length);
          next = patchSceneFields(next, block.sceneId, { content, inlineBlockIds });
        }
      }
    }
  }

  next = resyncBranchingConvergenceForMainScene(next, mainSceneId);
  next = pruneChapterInlineScenes(next, mainSceneId);

  if (willEmptyDedicatedScene) {
    next = cleanupEmptyMainScene(next, block.sceneId, onwardTarget);
  }
  return next;
}

export function moveFlatBlock(
  scenario: Scenario,
  _mainSceneId: string,
  fromFlatIndex: number,
  toFlatIndex: number,
  flatBlocks: ChapterFlatBlock[]
): Scenario {
  if (fromFlatIndex === toFlatIndex) return scenario;
  const moved = flatBlocks[fromFlatIndex];
  const target = flatBlocks[toFlatIndex];
  if (!moved || !target) return scenario;

  // Chaque bloc branchant vit dans sa propre scène : on ne réordonne qu'à l'intérieur
  // d'une même scène pour ne pas casser le chaînage entre scènes.
  if (moved.sceneId !== target.sceneId) return scenario;

  const scene = scenario.scenes.find((s) => s.id === moved.sceneId);
  if (!scene) return scenario;

  const withIds = ensureSceneBlockIds(scene);
  const content = [...withIds.content];
  const inlineBlockIds = [...withIds.inlineBlockIds!];
  const [movedItem] = content.splice(moved.contentIndex, 1);
  const [movedId] = inlineBlockIds.splice(moved.contentIndex, 1);
  content.splice(target.contentIndex, 0, movedItem!);
  inlineBlockIds.splice(target.contentIndex, 0, movedId!);

  const next: Scenario = {
    ...scenario,
    scenes: scenario.scenes.map((s) =>
      s.id === moved.sceneId ? { ...s, content, inlineBlockIds } : s
    ),
  };
  return resyncChoiceBranches(next, resolveMainSceneId(moved.sceneId));
}

export function appendFlatBlock(
  scenario: Scenario,
  mainSceneId: string,
  item: ContentItem
): Scenario {
  const scene = scenario.scenes.find((s) => s.id === mainSceneId);
  if (!scene) return scenario;

  const withIds = ensureSceneBlockIds(scene);
  const content = [...withIds.content, item];
  const inlineBlockIds = syncBlockIdsForContent(withIds.inlineBlockIds, content.length);

  let next: Scenario = {
    ...scenario,
    scenes: scenario.scenes.map((s) =>
      s.id === mainSceneId ? { ...s, content, inlineBlockIds } : s
    ),
  };
  return resyncChoiceBranches(next, mainSceneId);
}

/** Insère un bloc juste après un bloc existant (scène principale ou branche). */
export function insertBlockAfter(
  scenario: Scenario,
  afterBlock: ChapterFlatBlock,
  item: ContentItem
): Scenario {
  const scene = scenario.scenes.find((s) => s.id === afterBlock.sceneId);
  if (!scene) return scenario;

  const mainSceneId = resolveMainSceneId(afterBlock.sceneId);
  const withIds = ensureSceneBlockIds(scene);
  const insertAt = afterBlock.contentIndex + 1;
  const content = [...withIds.content];
  const inlineBlockIds = syncBlockIdsForContent(withIds.inlineBlockIds, content.length + 1);
  const newBlockId = inlineBlockIds[inlineBlockIds.length - 1];

  // Insertion en fin de scène : on transfère le lien de sortie vers le nouveau dernier bloc.
  let itemToInsert = item;
  if (insertAt >= content.length && content.length > 0) {
    const lastIdx = content.length - 1;
    const exit = exitGotoOf(content[lastIdx]!);
    if (exit !== undefined) {
      const relinked = withForwardGotoScene(itemToInsert, exit);
      if (relinked) {
        content[lastIdx] = clearExitGoto(content[lastIdx]!);
        itemToInsert = relinked;
      }
    }
  }

  content.splice(insertAt, 0, itemToInsert);
  inlineBlockIds.splice(insertAt, 0, newBlockId);
  inlineBlockIds.length = content.length;

  let next: Scenario = {
    ...scenario,
    scenes: scenario.scenes.map((s) =>
      s.id === afterBlock.sceneId ? { ...s, content, inlineBlockIds } : s
    ),
  };
  return resyncChoiceBranches(next, mainSceneId);
}

/** Types de blocs « branchants » : chacun vit dans sa propre scène (converge distincte). */
const BRANCHING_BLOCK_TYPES: ReadonlySet<string> = new Set(['choice', 'phoneChat', 'phoneCall']);

export function isBranchingBlockItem(item: ContentItem): boolean {
  return BRANCHING_BLOCK_TYPES.has(item.type);
}

function patchSceneFields(scenario: Scenario, sceneId: string, patch: Partial<Scene>): Scenario {
  return {
    ...scenario,
    scenes: scenario.scenes.map((s) => (s.id === sceneId ? { ...s, ...patch } : s)),
  };
}

function insertSceneAfter(scenario: Scenario, afterSceneId: string, scene: Scene): Scenario {
  const idx = scenario.scenes.findIndex((s) => s.id === afterSceneId);
  const scenes = [...scenario.scenes];
  if (idx < 0) scenes.push(scene);
  else scenes.splice(idx + 1, 0, scene);
  return { ...scenario, scenes };
}

/** Crée (si besoin) la scène `--converge` d'une scène branchante, juste après elle. */
function ensureConvergeSceneExists(scenario: Scenario, mainScene: Scene, convId: string): Scenario {
  if (scenario.scenes.some((s) => s.id === convId)) return scenario;
  const convScene: Scene = {
    id: convId,
    background: mainScene.background,
    elements: mainScene.elements,
    content: [],
    inlineBlockIds: [],
  };
  return insertSceneAfter(scenario, mainScene.id, convScene);
}

/**
 * Scène suivante IMMÉDIATE d'un bloc branchant (pas la sortie terminale récursive) :
 * - dialogue/overlay/appel-sms sans réponse : son `gotoScene` ;
 * - appel/sms/choix dont toutes les options mènent à une même scène « normale » (ni branche ni
 *   convergence) : cette scène. Sinon `undefined` (vrai branchement).
 */
function immediateBranchingOnward(item: ContentItem): string | undefined {
  const direct = exitGotoOf(item);
  if (direct !== undefined) return direct;
  const options = getBranchingOptions(item);
  if (options?.length) {
    const targets = options
      .map((o) => o.gotoScene)
      .filter((id): id is string => Boolean(id))
      .filter((id) => !isConvergeSceneId(id) && !isBranchSceneId(id));
    const first = targets[0];
    if (first !== undefined && targets.length === options.length && targets.every((t) => t === first)) {
      return first;
    }
  }
  return undefined;
}

/**
 * Réoriente la sortie d'un bloc branchant qui pointait directement vers `from` pour qu'elle
 * passe par sa convergence `to` (bloc branchant -> --converge [nouveau bloc] -> from).
 */
function redirectBranchingItemOnward(
  scenario: Scenario,
  sceneId: string,
  contentIndex: number,
  from: string,
  to: string
): Scenario {
  const scene = scenario.scenes.find((s) => s.id === sceneId);
  if (!scene) return scenario;
  const item = scene.content[contentIndex];
  if (!item) return scenario;

  let patched: ContentItem = item;
  if (item.type === 'phoneChat' || item.type === 'phoneCall') {
    if (item.replyOptions?.length) {
      patched = { ...item, replyOptions: redirectReplyOptionRefs(item.replyOptions, from, to) };
    } else if (item.gotoScene === from) {
      patched = { ...item, gotoScene: to };
    }
  } else if (item.type === 'choice') {
    patched = {
      ...item,
      options: item.options.map((o) => (o.gotoScene === from ? { ...o, gotoScene: to } : o)),
    };
  } else if (exitGotoOf(item) === from) {
    patched = withForwardGotoScene(item, to) ?? item;
  }

  if (patched === item) return scenario;
  const content = [...scene.content];
  content[contentIndex] = patched;
  return patchSceneFields(scenario, sceneId, { content });
}

/** Lien de sortie de scène (chaînage) porté par un bloc, s'il en supporte un. */
function exitGotoOf(item: ContentItem): string | undefined {
  if (item.type === 'dialogue' || item.type === 'timedOverlay') return item.gotoScene;
  if ((item.type === 'phoneChat' || item.type === 'phoneCall') && !item.replyOptions?.length) {
    return item.gotoScene;
  }
  return undefined;
}

function clearExitGoto(item: ContentItem): ContentItem {
  if (item.type === 'dialogue' || item.type === 'timedOverlay') return { ...item, gotoScene: undefined };
  if ((item.type === 'phoneChat' || item.type === 'phoneCall') && !item.replyOptions?.length) {
    return { ...item, gotoScene: undefined };
  }
  return item;
}

/**
 * Le lien de sortie d'une scène doit toujours rester sur son DERNIER bloc.
 * Quand on ajoute un bloc à la fin, on transfère le `gotoScene` de sortie de l'ancien
 * dernier bloc vers le nouveau (sinon le contenu ajouté serait sauté à l'exécution).
 */
function appendItemToScene(scenario: Scenario, sceneId: string, item: ContentItem): Scenario {
  const scene = scenario.scenes.find((s) => s.id === sceneId);
  if (!scene) return scenario;
  const withIds = ensureSceneBlockIds(scene);
  const content = [...withIds.content];

  let newItem = item;
  if (content.length > 0) {
    const lastIdx = content.length - 1;
    const exit = exitGotoOf(content[lastIdx]!);
    if (exit !== undefined) {
      const relinked = withForwardGotoScene(newItem, exit);
      if (relinked) {
        content[lastIdx] = clearExitGoto(content[lastIdx]!);
        newItem = relinked;
      }
    }
  }

  content.push(newItem);
  const inlineBlockIds = syncBlockIdsForContent(withIds.inlineBlockIds, content.length);
  return patchSceneFields(scenario, sceneId, { content, inlineBlockIds });
}

function internalLinkerDialogue(target: string): ContentItem {
  return {
    type: 'dialogue',
    text: '',
    isInternal: true,
    speaker: 'Héros (intérieur)',
    gotoScene: target,
  };
}

/** Pose un `gotoScene` de sortie directement sur le dernier bloc (si celui-ci le supporte). */
function withForwardGotoScene(item: ContentItem, target: string): ContentItem | null {
  if (item.type === 'dialogue') return { ...item, gotoScene: target };
  if (item.type === 'timedOverlay') return { ...item, gotoScene: target };
  if ((item.type === 'phoneChat' || item.type === 'phoneCall') && !item.replyOptions?.length) {
    return { ...item, gotoScene: target };
  }
  return null;
}

/**
 * Fait pointer la fin de la scène `sceneId` vers `targetSceneId` (chaînage du script).
 *
 * Si la scène se termine par un bloc branchant (sms/appel/choix), le chaînage passe par sa
 * scène `--converge` : le bloc branchant reste isolé, et c'est la convergence qui pointe vers
 * la scène suivante.
 */
function linkSceneForward(scenario: Scenario, sceneId: string, targetSceneId: string): Scenario {
  const scene = scenario.scenes.find((s) => s.id === sceneId);
  if (!scene) return scenario;
  const withIds = ensureSceneBlockIds(scene);

  const last = withIds.content[withIds.content.length - 1];
  if (last && isBranchingBlockItem(last)) {
    const convId = convergeSceneId(sceneId);
    const next = ensureConvergeSceneExists(scenario, scene, convId);
    return linkNarrativeSceneForward(next, convId, targetSceneId);
  }

  return linkNarrativeSceneForward(scenario, sceneId, targetSceneId);
}

function linkNarrativeSceneForward(
  scenario: Scenario,
  sceneId: string,
  targetSceneId: string
): Scenario {
  const scene = scenario.scenes.find((s) => s.id === sceneId);
  if (!scene) return scenario;
  const withIds = ensureSceneBlockIds(scene);

  if (withIds.content.length === 0) {
    return patchSceneFields(scenario, sceneId, {
      content: [internalLinkerDialogue(targetSceneId)],
      inlineBlockIds: [uniqueId('blk')],
    });
  }

  const lastIdx = withIds.content.length - 1;
  const lastItem = withIds.content[lastIdx]!;

  const linked = withForwardGotoScene(lastItem, targetSceneId);
  if (linked) {
    const content = [...withIds.content];
    content[lastIdx] = linked;
    return patchSceneFields(scenario, sceneId, { content });
  }

  // Bloc branchant avec réponses / choix, ou bloc non chaînable (commande, overlay) :
  // on ajoute un lien inline, qui devient le contenu de convergence géré par le resync.
  const content = [...withIds.content, internalLinkerDialogue(targetSceneId)];
  const inlineBlockIds = syncBlockIdsForContent(withIds.inlineBlockIds, content.length);
  return patchSceneFields(scenario, sceneId, { content, inlineBlockIds });
}

/**
 * Ajoute un bloc à la fin d'un chapitre.
 * Un bloc branchant (choix/SMS/appel) crée sa propre scène, avec chaînage automatique
 * du bloc précédent vers elle — chaque bloc branchant a donc sa convergence distincte.
 */
export function appendChapterBlock(
  scenario: Scenario,
  chapterScenes: Scene[],
  item: ContentItem
): Scenario {
  const mainScenes = getChapterMainScenes(chapterScenes);
  if (mainScenes.length === 0) {
    const fallbackId = chapterScenes[0]?.id;
    return fallbackId ? appendFlatBlock(scenario, fallbackId, item) : scenario;
  }

  const lastMain = mainScenes[mainScenes.length - 1]!;
  const withIds = ensureSceneBlockIds(lastMain);
  const chapterEmpty = mainScenes.length === 1 && withIds.content.length === 0;

  // Contenu narratif.
  if (!isBranchingBlockItem(item)) {
    const last = withIds.content[withIds.content.length - 1];
    // Si la dernière scène est une scène branchante isolée, le narratif va dans sa convergence.
    if (last && isBranchingBlockItem(last)) {
      const convId = convergeSceneId(lastMain.id);
      let next = ensureConvergeSceneExists(scenario, lastMain, convId);
      next = appendItemToScene(next, convId, item);
      return resyncChoiceBranches(next, lastMain.id);
    }
    return resyncChoiceBranches(appendItemToScene(scenario, lastMain.id, item), lastMain.id);
  }

  // Premier bloc branchant d'un chapitre vide : il occupe la scène de départ.
  if (chapterEmpty) {
    return resyncChoiceBranches(appendItemToScene(scenario, lastMain.id, item), lastMain.id);
  }

  const newSceneId = uniqueId('scene');
  let next = insertSceneAfter(scenario, lastMain.id, {
    id: newSceneId,
    background: lastMain.background,
    elements: lastMain.elements,
    content: [item],
    inlineBlockIds: [uniqueId('blk')],
  });

  next = linkSceneForward(next, lastMain.id, newSceneId);
  next = resyncChoiceBranches(next, lastMain.id);
  return resyncChoiceBranches(next, newSceneId);
}

/**
 * Insère un bloc après un bloc existant.
 * Un bloc branchant est isolé dans une nouvelle scène : le bloc d'avant est chaîné vers elle
 * et le contenu qui suivait passe (inline) après le nouveau bloc, comme sa convergence.
 */
export function insertChapterBlockAfter(
  scenario: Scenario,
  afterBlock: ChapterFlatBlock,
  item: ContentItem
): Scenario {
  if (!isBranchingBlockItem(item)) {
    // Narratif inséré juste après un bloc branchant : il devient la tête de sa convergence.
    if (isBranchingBlockItem(afterBlock.item) && !isBranchSceneId(afterBlock.sceneId)) {
      const branchingScene = scenario.scenes.find((s) => s.id === afterBlock.sceneId);
      if (branchingScene) {
        const convId = convergeSceneId(afterBlock.sceneId);
        // La convergence n'existe pas encore (ex. bloc branchant relié directement à la scène
        // suivante) : le bloc inséré devient la tête de la convergence et hérite du lien de sortie
        // courant, et le bloc branchant est rebranché vers sa convergence
        // (bloc branchant -> --converge [nouveau bloc] -> scène suivante).
        const existingConv = scenario.scenes.find((s) => s.id === convId);
        const convWasEmpty = !existingConv || (existingConv.content?.length ?? 0) === 0;
        let next: Scenario = scenario;
        let itemToInsert = item;
        if (convWasEmpty) {
          const onward = immediateBranchingOnward(afterBlock.item);
          if (onward !== undefined && onward !== convId) {
            itemToInsert = withForwardGotoScene(item, onward) ?? item;
            next = redirectBranchingItemOnward(
              next,
              afterBlock.sceneId,
              afterBlock.contentIndex,
              onward,
              convId
            );
          }
        }
        next = ensureConvergeSceneExists(next, branchingScene, convId);
        const convScene = ensureSceneBlockIds(next.scenes.find((s) => s.id === convId)!);
        const content = [itemToInsert, ...convScene.content];
        const inlineBlockIds = syncBlockIdsForContent(
          [uniqueId('blk'), ...(convScene.inlineBlockIds ?? [])],
          content.length
        );
        next = patchSceneFields(next, convId, { content, inlineBlockIds });
        return resyncChoiceBranches(next, afterBlock.sceneId);
      }
    }
    return insertBlockAfter(scenario, afterBlock, item);
  }

  const anchorScene = scenario.scenes.find((s) => s.id === afterBlock.sceneId);
  if (!anchorScene) return scenario;

  const withIds = ensureSceneBlockIds(anchorScene);
  const cut = afterBlock.contentIndex + 1;
  const tailItems = withIds.content.slice(cut);
  const tailIds = withIds.inlineBlockIds!.slice(cut);
  const newSceneId = uniqueId('scene');
  const mainSceneId = resolveMainSceneId(anchorScene.id);
  const newBlockIds = syncBlockIdsForContent([uniqueId('blk'), ...tailIds], tailItems.length + 1);

  let next = patchSceneFields(scenario, anchorScene.id, {
    content: withIds.content.slice(0, cut),
    inlineBlockIds: withIds.inlineBlockIds!.slice(0, cut),
  });

  next = insertSceneAfter(next, anchorScene.id, {
    id: newSceneId,
    background: anchorScene.background,
    elements: anchorScene.elements,
    content: [item, ...tailItems],
    inlineBlockIds: newBlockIds,
  });

  next = linkSceneForward(next, anchorScene.id, newSceneId);
  next = resyncChoiceBranches(next, mainSceneId);
  return resyncChoiceBranches(next, newSceneId);
}

export function appendBranchBlock(
  scenario: Scenario,
  branchSceneId: string,
  item: ContentItem
): Scenario {
  const scene = scenario.scenes.find((s) => s.id === branchSceneId);
  if (!scene) return scenario;

  const mainSceneId = branchSceneId.split('--')[0];
  const withIds = ensureSceneBlockIds(scene);
  const content = [...withIds.content, item];
  const inlineBlockIds = syncBlockIdsForContent(withIds.inlineBlockIds, content.length);

  let next: Scenario = {
    ...scenario,
    scenes: scenario.scenes.map((s) =>
      s.id === branchSceneId ? { ...s, content, inlineBlockIds } : s
    ),
  };
  return resyncChoiceBranches(next, mainSceneId);
}

export function removeBranchBlock(scenario: Scenario, block: ChapterFlatBlock): Scenario {
  if (!isBranchSceneId(block.sceneId) || isConvergeSceneId(block.sceneId)) {
    return removeFlatBlock(scenario, block);
  }

  const mainSceneId = resolveMainSceneId(block.sceneId);
  const branchScene = scenario.scenes.find((s) => s.id === block.sceneId);
  if (!branchScene) return scenario;

  const branchWithIds = ensureSceneBlockIds(branchScene);
  const removedBlockId = branchWithIds.inlineBlockIds![block.contentIndex];
  const isFirstBlock = block.contentIndex === 0;
  const link = findBranchLink(scenario, mainSceneId, block.sceneId, removedBlockId);

  let next: Scenario = {
    ...scenario,
    scenes: scenario.scenes.map((scene) => {
      if (scene.id !== block.sceneId) return scene;
      return {
        ...scene,
        content: scene.content.filter((_, i) => i !== block.contentIndex),
        inlineBlockIds: scene.inlineBlockIds?.filter((_, i) => i !== block.contentIndex),
      };
    }),
  };

  const branchAfter = next.scenes.find((s) => s.id === block.sceneId);
  const branchEmpty = !branchAfter || branchAfter.content.length === 0;

  if (link) {
    if (branchEmpty) {
      next = patchBranchingOption(next, mainSceneId, link.choiceContentIndex, link.optionIndex, {
        gotoBlockId: undefined,
        gotoScene: undefined,
        continueInScene: undefined,
      });
      if (removedBlockId) {
        next = removeMainSceneBlockByBlockId(next, mainSceneId, removedBlockId);
      }
      next = removeSceneById(next, block.sceneId);
    } else if (isFirstBlock) {
      const updatedBranch = ensureSceneBlockIds(branchAfter!);
      const newFirstBlockId = updatedBranch.inlineBlockIds![0];
      next = patchBranchingOption(next, mainSceneId, link.choiceContentIndex, link.optionIndex, {
        gotoBlockId: newFirstBlockId,
        gotoScene: block.sceneId,
      });
      if (removedBlockId) {
        next = removeMainSceneBlockByBlockId(next, mainSceneId, removedBlockId);
      }
    }
  } else if (branchEmpty) {
    next = removeSceneById(next, block.sceneId);
  }

  next = resyncBranchingConvergenceForMainScene(next, mainSceneId);
  next = pruneChapterInlineScenes(next, mainSceneId);
  return next;
}

export function moveBranchBlock(
  scenario: Scenario,
  branchSceneId: string,
  fromIndex: number,
  toIndex: number
): Scenario {
  if (fromIndex === toIndex) return scenario;
  const scene = scenario.scenes.find((s) => s.id === branchSceneId);
  if (!scene) return scenario;

  const mainSceneId = branchSceneId.split('--')[0];
  const withIds = ensureSceneBlockIds(scene);
  const content = [...withIds.content];
  const inlineBlockIds = [...(withIds.inlineBlockIds ?? [])];
  const [movedItem] = content.splice(fromIndex, 1);
  const [movedId] = inlineBlockIds.splice(fromIndex, 1);
  content.splice(toIndex, 0, movedItem);
  inlineBlockIds.splice(toIndex, 0, movedId);

  let next: Scenario = {
    ...scenario,
    scenes: scenario.scenes.map((s) =>
      s.id === branchSceneId ? { ...s, content, inlineBlockIds } : s
    ),
  };
  return resyncChoiceBranches(next, mainSceneId);
}

export function appendConvergenceBlock(
  scenario: Scenario,
  mainSceneId: string,
  branchingContentIndex: number,
  item: ContentItem
): Scenario {
  const scene = scenario.scenes.find((s) => s.id === mainSceneId);
  if (!scene) return scenario;

  const withIds = ensureSceneBlockIds(scene);
  const branchingItem = withIds.content[branchingContentIndex];
  const options = getBranchingOptions(branchingItem);
  if (!options) return appendFlatBlock(scenario, mainSceneId, item);

  const branchEntryIds = getBranchEntryBlockIds(
    scenario,
    mainSceneId,
    branchingContentIndex,
    options
  );
  const branchEntryIndices: number[] = [];
  withIds.inlineBlockIds!.forEach((bid, idx) => {
    if (idx > branchingContentIndex && branchEntryIds.has(bid)) branchEntryIndices.push(idx);
  });
  const poolEnd =
    branchEntryIndices.length > 0
      ? Math.max(...branchEntryIndices)
      : branchingContentIndex;

  let insertAt = poolEnd + 1;
  while (insertAt < withIds.content.length && !isChapterSegmentBoundary(withIds.content[insertAt])) {
    insertAt++;
  }

  const content = [...withIds.content];
  const inlineBlockIds = syncBlockIdsForContent(withIds.inlineBlockIds, content.length + 1);
  const newBlockId = inlineBlockIds[inlineBlockIds.length - 1];
  content.splice(insertAt, 0, item);
  inlineBlockIds.splice(insertAt, 0, newBlockId);
  inlineBlockIds.length = content.length;

  let next: Scenario = {
    ...scenario,
    scenes: scenario.scenes.map((s) =>
      s.id === mainSceneId ? { ...s, content, inlineBlockIds } : s
    ),
  };
  return resyncChoiceBranches(next, mainSceneId);
}

export function listLinkablePoolBlocks(pool: ChapterFlatBlock[]): InlineBlockRef[] {
  return pool.map((b) => ({
    blockId: b.blockId,
    sceneId: b.sceneId,
    contentIndex: b.contentIndex,
    label: getContentItemLabel(b.item),
    summary: getContentItemSummary(b.item),
  }));
}

export function startChoiceBranchWithDialogue(
  scenario: Scenario,
  mainSceneId: string,
  branchingContentIndex: number,
  optionIndex: number,
  item: ContentItem = { type: 'dialogue', text: '', isInternal: true, speaker: 'Héros (intérieur)' }
): Scenario {
  const scene = scenario.scenes.find((s) => s.id === mainSceneId);
  if (!scene) return scenario;
  const branchingItem = scene.content[branchingContentIndex];
  const options = getBranchingOptions(branchingItem);
  if (!options) return scenario;

  const withIds = ensureSceneBlockIds(scene);
  const content = [...withIds.content];
  const inlineBlockIds = syncBlockIdsForContent(withIds.inlineBlockIds, content.length + 1);
  const newBlockId = inlineBlockIds[inlineBlockIds.length - 1];

  let insertAt = branchingContentIndex + 1;
  const branchEntryIds = getBranchEntryBlockIds(
    scenario,
    mainSceneId,
    branchingContentIndex,
    options
  );
  while (
    insertAt < content.length &&
    branchEntryIds.has(inlineBlockIds[insertAt] ?? '')
  ) {
    insertAt++;
  }

  content.splice(insertAt, 0, item);
  inlineBlockIds.splice(insertAt, 0, newBlockId);
  inlineBlockIds.length = content.length;

  let next: Scenario = {
    ...scenario,
    scenes: scenario.scenes.map((s) =>
      s.id === mainSceneId ? { ...s, content, inlineBlockIds } : s
    ),
  };
  return applyChoiceBlockLink(next, mainSceneId, branchingContentIndex, optionIndex, newBlockId);
}
